import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ArrowLeft } from '@travel/icons/ui';
import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import useDialogHandler from '@travel/traveler-core/hooks/useDialogHandler';
import { Dialog } from '@travel/ui';
import { GalleryMedia } from '@travel/ui/components/Gallery';
import { cx, isNotEmptyArray } from '@travel/utils';

import Media from 'components/Media';
import PhotoGalleryDetail from 'components/PhotoGalleryDetail';

import { Translate } from 'core/translate';
import { getFilterData, getGridAutoLayout, getImageType, removeGridType } from './helper';

import styles from './photoGallery.module.scss';

type GridProps = {
  galleryData: GalleryMedia[];
  initImageIndex?: number;
};

function Grid({ galleryData, initImageIndex }: GridProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [selectedFilter, setSelectedFilter] = useState('All');
  const [imageIndex, setImageIndex] = useState(initImageIndex || 0);
  const { isOpen, onOpen, onClose } = useDialogHandler(false);

  const filterList = getFilterData(galleryData);
  const hasNoFilter = filterList.length === 1 && filterList[0] === 'All';

  const filteredData =
    selectedFilter !== 'All'
      ? galleryData.filter(item => item.category === selectedFilter)
      : galleryData;

  const dataWithGridType = filteredData.map((item, index) => {
    const type = index === 0 ? 'large' : getImageType(item.size);
    return { ...item, gridType: type };
  });

  const autoLayoutData = useMemo(() => getGridAutoLayout(dataWithGridType), [dataWithGridType]);

  const handleClickFilter = (filter: string) => {
    setSelectedFilter(filter);
  };

  useEffect(() => {
    if (imageIndex) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 200);
    }
  }, [imageIndex]);

  return (
    <>
      {!hasNoFilter && (
        <div className={styles.gridFilter} data-testid="photoGallery-filter">
          {filterList.map((item, index) => (
            <button
              className={cx(styles.filterItem, selectedFilter === item ? styles.active : null)}
              onClick={() => handleClickFilter(item)}
              key={`${item}-${index}`}
              data-testid={`photoGallery-filter-${item}`}
            >
              {item}
            </button>
          ))}
        </div>
      )}
      <div className={styles.gridWrapper} data-testid="grid-wrapper">
        {autoLayoutData.map((media, index) => {
          const handleOnClick = () => {
            setImageIndex(index);
            onOpen();
          };
          const shouldDisableLazyload = initImageIndex ? index <= initImageIndex + 4 : index <= 4;

          return (
            <div
              className={styles[media.gridType || '']}
              ref={index === imageIndex ? ref : null}
              key={`${media.name}-${index}`}
            >
              <Media
                wrapperClassName={styles.mediaWrapper}
                key={`${media.url}-${index}`}
                media={removeGridType(media)}
                sizes="100vw"
                onClick={handleOnClick}
                isDisableLazyload={shouldDisableLazyload}
              />
            </div>
          );
        })}
      </div>
      <PhotoGalleryDetail
        isOpen={isOpen}
        onClose={onClose}
        imageIndex={imageIndex}
        mediaData={autoLayoutData}
      />
    </>
  );
}

type Props = {
  galleryData: GalleryMedia[];
  isOpenPhotoGallery: boolean;
  onClosePhotoGallery: () => void;
  initImageIndex?: number;
};

function PhotoGallery({
  galleryData,
  isOpenPhotoGallery,
  onClosePhotoGallery,
  initImageIndex,
}: Props) {
  const isSP = useDeviceType() === MOBILE_SCREEN;

  return isNotEmptyArray(galleryData) && isOpenPhotoGallery ? (
    <Dialog
      title={isSP ? <Translate id="Provider_Info.Photos.Title" /> : null}
      isOpen={isOpenPhotoGallery}
      onClose={onClosePhotoGallery}
      wrapperClassName={styles.gridDialog}
      className={styles.dialogWrapper}
      hasEnableBack={false}
      isUseContentFlexBox={false}
      contentClassName={styles.gridDialogContent}
    >
      <div className={styles.wrapper}>
        {!isSP && (
          <div className={styles.actionBar}>
            <button
              data-testid="back-button"
              type="button"
              className={styles.backButton}
              onClick={onClosePhotoGallery}
              aria-label="back button"
            >
              <ArrowLeft size={24} />
            </button>
          </div>
        )}
        <Grid galleryData={galleryData} initImageIndex={initImageIndex} />
      </div>
    </Dialog>
  ) : null;
}

export default PhotoGallery;
